<template>
  <div class="flex flex-col h-full bg-slate-950 overflow-hidden border-t lg:border-t-0 border-slate-800 select-none">
    <!-- Notebook Top Toolbar (Jupyter / Colab Style) -->
    <div class="bg-slate-900 border-b border-slate-800 px-3 py-2 shrink-0 shadow-sm space-y-2">
      <div class="flex items-center justify-between gap-2 min-w-0">
        <span class="text-xs font-bold text-amber-400 flex items-center font-mono shrink-0 whitespace-nowrap">
          <i class="pi pi-book mr-1.5 text-unibo-red"></i>
          Jupyter Notebook
        </span>
        <span
          class="text-[10px] font-sans px-2 py-0.5 rounded-full border whitespace-nowrap transition-opacity duration-200"
          :class="[
            saveHint ? 'opacity-100' : 'opacity-0',
            saveHint === 'Saved'
              ? 'text-emerald-400/90 border-emerald-500/20 bg-emerald-500/10'
              : 'text-slate-400 border-slate-700/60 bg-slate-800/80',
          ]"
          aria-live="polite"
        >
          {{ saveHint || 'Saved' }}
        </span>
      </div>

      <div class="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          @click="addCell('code')"
          class="shrink-0 whitespace-nowrap px-2.5 py-1 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 rounded-lg text-xs font-semibold transition-colors inline-flex items-center gap-1.5 border border-emerald-500/40 shadow-sm cursor-pointer"
        >
          <i class="pi pi-plus text-[10px]"></i>
          <span>Code</span>
        </button>

        <button
          type="button"
          @click="addCell('markdown')"
          class="shrink-0 whitespace-nowrap px-2.5 py-1 bg-sky-600/20 hover:bg-sky-600/30 text-sky-300 rounded-lg text-xs font-semibold transition-colors inline-flex items-center gap-1.5 border border-sky-500/40 shadow-sm cursor-pointer"
        >
          <i class="pi pi-plus text-[10px]"></i>
          <span>Text</span>
        </button>

        <button
          type="button"
          @click="runAllCells"
          :disabled="isAnyRunning"
          class="shrink-0 whitespace-nowrap px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-xs font-bold transition-colors inline-flex items-center gap-1.5 shadow-sm cursor-pointer"
        >
          <i :class="isAnyRunning ? 'pi pi-spin pi-spinner text-xs' : 'pi pi-forward text-xs'"></i>
          <span>Run All</span>
        </button>

        <div class="w-px h-5 bg-slate-700 mx-0.5 self-center shrink-0" aria-hidden="true" />

        <button
          type="button"
          @click="exportIpynb"
          class="shrink-0 whitespace-nowrap px-2.5 py-1 bg-violet-600/20 hover:bg-violet-600/30 text-violet-300 rounded-lg text-xs font-semibold transition-colors inline-flex items-center gap-1.5 border border-violet-500/40 shadow-sm cursor-pointer"
          v-tooltip.bottom="'Download notebook as .ipynb'"
        >
          <i class="pi pi-download text-[10px]"></i>
          <span>Export</span>
        </button>

        <button
          type="button"
          @click="triggerImportIpynb"
          class="shrink-0 whitespace-nowrap px-2.5 py-1 bg-sky-600/20 hover:bg-sky-600/30 text-sky-300 rounded-lg text-xs font-semibold transition-colors inline-flex items-center gap-1.5 border border-sky-500/40 shadow-sm cursor-pointer"
          v-tooltip.bottom="'Import a Jupyter .ipynb file'"
        >
          <i class="pi pi-upload text-[10px]"></i>
          <span>Import</span>
        </button>
        <input
          ref="ipynbFileInput"
          type="file"
          accept=".ipynb,application/x-ipynb+json,application/json"
          class="hidden"
          @change="onIpynbFileSelected"
        />

        <div class="w-px h-5 bg-slate-700 mx-0.5 self-center shrink-0" aria-hidden="true" />

        <button
          type="button"
          @click="restartKernel"
          class="shrink-0 whitespace-nowrap px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs transition-colors inline-flex items-center gap-1.5 border border-slate-700 cursor-pointer"
          v-tooltip.bottom="'Restart Python Kernel (Reset Variables)'"
        >
          <i class="pi pi-refresh text-xs text-amber-400"></i>
          <span>Restart</span>
        </button>

        <button
          type="button"
          @click="clearAllOutputs"
          class="shrink-0 whitespace-nowrap px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs transition-colors inline-flex items-center gap-1.5 border border-slate-700 cursor-pointer"
          v-tooltip.bottom="'Clear All Cell Outputs'"
        >
          <i class="pi pi-trash text-xs text-rose-400"></i>
          <span>Clear</span>
        </button>

        <button
          type="button"
          @click="$emit('toggle-view')"
          class="shrink-0 whitespace-nowrap px-2.5 py-1 bg-unibo-red/20 text-unibo-red border border-unibo-red/40 hover:bg-unibo-red/30 rounded-lg text-xs font-mono font-medium transition-colors inline-flex items-center gap-1.5 cursor-pointer ml-auto"
          v-tooltip.bottom="'Switch to Single Script Editor'"
        >
          <i class="pi pi-file-code text-xs"></i>
          <span>Script</span>
        </button>
      </div>
    </div>

    <!-- Notebook Cells Stack Container -->
    <div class="flex-1 overflow-y-auto p-4 space-y-4 font-mono select-text">
      <div 
        v-for="(cell, index) in cells" 
        :key="cell.id"
        class="bg-slate-900 border rounded-xl overflow-hidden transition-all shadow-md group"
        :class="cell.isRunning ? 'border-amber-500/80 ring-1 ring-amber-500/40' : 'border-slate-800 hover:border-slate-700'"
      >
        <div class="bg-slate-950/90 px-3 py-1.5 border-b border-slate-800/80 flex items-center justify-between text-xs select-none">
          <div class="flex items-center space-x-2">
            <span class="font-mono text-xs font-bold" :class="cell.isRunning ? 'text-amber-400 animate-pulse' : 'text-slate-400'">
              In [{{ cell.isRunning ? '*' : (cell.executionCount || ' ') }}]:
            </span>
            <span class="text-[10px] px-1.5 py-0.5 rounded uppercase font-semibold font-sans tracking-wide bg-slate-800 text-slate-300">
              {{ cell.type === 'code' ? 'Code Cell' : 'Markdown Cell' }}
            </span>
          </div>

          <div class="flex items-center space-x-1">
            <button 
              v-if="cell.type === 'code'"
              type="button"
              @click="runCell(index)"
              :disabled="cell.isRunning"
              class="px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[11px] font-sans font-semibold transition-colors flex items-center space-x-1 shadow-xs cursor-pointer"
              v-tooltip.bottom="'Run Cell (Shift+Enter)'"
            >
              <i :class="cell.isRunning ? 'pi pi-spin pi-spinner text-[10px]' : 'pi pi-play text-[10px]'"></i>
              <span>Run</span>
            </button>

            <button 
              type="button"
              @click="moveCell(index, -1)" 
              :disabled="index === 0"
              class="p-1 hover:bg-slate-800 text-slate-400 hover:text-slate-200 rounded disabled:opacity-30 cursor-pointer"
              v-tooltip.bottom="'Move Cell Up'"
            >
              <i class="pi pi-arrow-up text-[10px]"></i>
            </button>

            <button 
              type="button"
              @click="moveCell(index, 1)" 
              :disabled="index === cells.length - 1"
              class="p-1 hover:bg-slate-800 text-slate-400 hover:text-slate-200 rounded disabled:opacity-30 cursor-pointer"
              v-tooltip.bottom="'Move Cell Down'"
            >
              <i class="pi pi-arrow-down text-[10px]"></i>
            </button>

            <button 
              type="button"
              @click="addCell('code', index + 1)"
              class="p-1 hover:bg-slate-800 text-slate-400 hover:text-emerald-400 rounded cursor-pointer"
              v-tooltip.bottom="'Add Code Cell Below'"
            >
              <i class="pi pi-plus text-[10px]"></i>
            </button>

            <button 
              type="button"
              @click="deleteCell(index)"
              :disabled="cells.length === 1"
              class="p-1 hover:bg-slate-800 text-slate-400 hover:text-rose-400 rounded disabled:opacity-30 cursor-pointer"
              v-tooltip.bottom="'Delete Cell'"
            >
              <i class="pi pi-trash text-[10px]"></i>
            </button>
          </div>
        </div>

        <div class="bg-[#0d1321] border-t border-slate-900/60">
          <NotebookCellEditor
            :model-value="cell.code"
            :language="cell.type === 'markdown' ? 'markdown' : 'python'"
            :disabled="!!cell.isRunning"
            :min-height="cell.type === 'markdown' ? 72 : 112"
            @update:model-value="(val) => onCellEditorUpdate(index, val)"
            @run="runCell(index)"
          />
        </div>

        <div 
          v-if="cell.output && (cell.output.stdout || cell.output.stderr || cell.output.error || cell.output.plots?.length)" 
          class="bg-slate-950 border-t border-slate-800 p-3 space-y-3 font-mono text-xs overflow-x-auto select-text"
        >
          <div v-if="cell.output.stdout" class="text-slate-200 whitespace-pre-wrap leading-relaxed">
            <pre>{{ cell.output.stdout }}</pre>
          </div>
          <div v-if="cell.output.stderr" class="text-amber-400 whitespace-pre-wrap">
            <pre>{{ cell.output.stderr }}</pre>
          </div>
          <div v-if="cell.output.error" class="bg-rose-950/40 p-3 rounded border border-rose-800/60 text-rose-300 whitespace-pre-wrap">
            <pre>{{ cell.output.error }}</pre>
          </div>
          <div v-if="cell.output.plots?.length" class="space-y-3 pt-2">
            <div 
              v-for="(plot, pIdx) in cell.output.plots" 
              :key="pIdx"
              class="bg-white p-2 rounded-lg border border-slate-800 max-w-2xl"
            >
              <img :src="plot" :alt="`Notebook Plot ${pIdx+1}`" class="w-full h-auto" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount } from 'vue';
import { NotebookCell } from '../types';
import { runPythonCode, resetPythonKernel } from '../services/pyodide';
import { serializeNotebookCells } from '../services/storage';
import { downloadIpynb, readIpynbFile } from '../services/ipynb';
import NotebookCellEditor from './NotebookCellEditor.vue';

const props = defineProps<{
  initialCode: string;
  savedCells?: NotebookCell[];
  /** Used for .ipynb download filename (without extension). */
  notebookName?: string;
  notebookTitle?: string;
}>();

const emit = defineEmits<{
  (e: 'update:cells', cells: NotebookCell[]): void;
  (e: 'update:code', code: string): void;
  (e: 'toggle-view'): void;
}>();

const globalExecutionCounter = ref(1);
const saveHint = ref('');
const ipynbFileInput = ref<HTMLInputElement | null>(null);
let syncTimer: number | null = null;

function newCellId(prefix = 'cell') {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Split saved script text into notebook cells (fallback when cells[] missing). */
function parseCodeToCells(rawCode: string): NotebookCell[] {
  if (!rawCode || !rawCode.trim()) {
    return [{
      id: newCellId(),
      type: 'code',
      code: '# Write your Python code here...\n',
    }];
  }

  // Split before lines that look like "# Cell N" (at start of string or after newline)
  const parts = rawCode.split(/\n(?=#\s*Cell\s*\d+)/i);
  const parsed = parts
    .map((part) => {
      let code = part.replace(/^\s+/, '');
      // Remove the marker line itself — cell body should not include "# Cell N"
      code = code.replace(/^#\s*Cell\s*\d+[^\n]*\n?/i, '');
      return code.replace(/\s+$/, '');
    })
    .filter((code) => code.length > 0)
    .map((code) => ({
      id: newCellId(),
      type: 'code' as const,
      code,
    }));

  if (parsed.length > 0) return parsed;

  return [{
    id: newCellId(),
    type: 'code',
    code: rawCode,
  }];
}

function initCells(): NotebookCell[] {
  if (props.savedCells && props.savedCells.length > 0) {
    return serializeNotebookCells(props.savedCells).map((c) => ({
      ...c,
      isRunning: false,
    }));
  }
  return parseCodeToCells(props.initialCode);
}

const cells = ref<NotebookCell[]>(initCells());

const isAnyRunning = computed(() => cells.value.some((c) => c.isRunning));

function cellsForPersist(): NotebookCell[] {
  return serializeNotebookCells(cells.value);
}

function combinedCodeFromCells(list: NotebookCell[]): string {
  return list
    .map((c, i) => `# Cell ${i + 1}\n${c.code}`)
    .join('\n\n');
}

function flushSync() {
  const plain = cellsForPersist();
  const combined = combinedCodeFromCells(plain);
  emit('update:code', combined);
  emit('update:cells', plain);
  saveHint.value = 'Saved';
  window.setTimeout(() => {
    if (saveHint.value === 'Saved') saveHint.value = '';
  }, 1200);
}

function scheduleSync(immediate = false) {
  if (syncTimer) {
    window.clearTimeout(syncTimer);
    syncTimer = null;
  }
  saveHint.value = 'Saving…';
  if (immediate) {
    flushSync();
    return;
  }
  syncTimer = window.setTimeout(() => {
    syncTimer = null;
    flushSync();
  }, 350);
}

function onCellEditorUpdate(index: number, value: string) {
  if (!cells.value[index]) return;
  cells.value[index].code = value;
  scheduleSync(false);
}

function addCell(type: 'code' | 'markdown', atIndex?: number) {
  const newCell: NotebookCell = {
    id: newCellId(),
    type,
    code: type === 'code'
      ? '# Write python code here\n'
      : '### Notes\n',
  };

  if (atIndex !== undefined && atIndex >= 0 && atIndex <= cells.value.length) {
    cells.value.splice(atIndex, 0, newCell);
  } else {
    cells.value.push(newCell);
  }
  scheduleSync(true);
}

function deleteCell(index: number) {
  if (cells.value.length > 1) {
    cells.value.splice(index, 1);
    scheduleSync(true);
  }
}

function moveCell(index: number, direction: number) {
  const targetIndex = index + direction;
  if (targetIndex >= 0 && targetIndex < cells.value.length) {
    const temp = cells.value[index];
    cells.value[index] = cells.value[targetIndex];
    cells.value[targetIndex] = temp;
    scheduleSync(true);
  }
}

async function runCell(index: number) {
  const cell = cells.value[index];
  if (!cell || cell.isRunning || cell.type !== 'code') return;
  // Persist latest edits before running
  scheduleSync(true);

  cell.isRunning = true;
  cell.executionCount = globalExecutionCounter.value++;

  try {
    const res = await runPythonCode(cell.code, false);
    cell.output = res;
  } catch (e: any) {
    cell.output = {
      stdout: '',
      stderr: e.message || String(e),
      error: e.stack || String(e),
      plots: [],
      executionTimeMs: 0,
      timestamp: new Date().toISOString(),
    };
  } finally {
    cell.isRunning = false;
    scheduleSync(true);
  }
}

function exportIpynb() {
  scheduleSync(true);
  const name = props.notebookName || 'notebook';
  downloadIpynb(cellsForPersist(), name, props.notebookTitle);
}

function triggerImportIpynb() {
  ipynbFileInput.value?.click();
}

async function onIpynbFileSelected(e: Event) {
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0];
  // Allow re-selecting the same file later
  input.value = '';
  if (!file) return;

  try {
    const imported = await readIpynbFile(file);
    if (
      cells.value.some((c) => c.code.trim()) &&
      !confirm('Replace the current notebook cells with the imported .ipynb? Unsaved edits in this view will be lost.')
    ) {
      return;
    }
    cells.value = imported.map((c) => ({ ...c, isRunning: false }));
    const maxExec = imported.reduce((max, c) => Math.max(max, c.executionCount || 0), 0);
    globalExecutionCounter.value = Math.max(1, maxExec + 1);
    scheduleSync(true);
  } catch (err: any) {
    alert(err?.message || 'Failed to import .ipynb file.');
  }
}

async function runAllCells() {
  for (let i = 0; i < cells.value.length; i++) {
    if (cells.value[i].type === 'code') {
      await runCell(i);
    }
  }
}

async function restartKernel() {
  await resetPythonKernel();
  globalExecutionCounter.value = 1;
  for (const cell of cells.value) {
    cell.executionCount = undefined;
    cell.output = undefined;
  }
  scheduleSync(true);
}

function clearAllOutputs() {
  for (const cell of cells.value) {
    cell.output = undefined;
  }
  scheduleSync(true);
}

onBeforeUnmount(() => {
  if (syncTimer) {
    window.clearTimeout(syncTimer);
    syncTimer = null;
  }
  // App.vue flushes IndexedDB before question switches / unload.
  // Avoid emitting here — activeQuestionId may already have changed.
});
</script>
